import { useEffect } from "react";
import { useLocation } from "react-router";

import { DEVICE_TOKEN_QUERY_KEY } from "~/features/push-notifications/use-native-subscription";
import { sharedLocalStorage } from "~/features/storage/local.client";
import {
  isTwaPortReady,
  isTwaTwoWayCommunicationSupported,
  sendTwaMessage,
} from "~/features/twa/message-channel.client";
import { useHydrated } from "~/hooks/use-hydrated";
import { useTwa } from "~/hooks/use-twa";
import { DEVICE_TOKEN_PREFIX } from "../push-notifications/constants";

import { datadogRum } from "@datadog/browser-rum";
import { useQueryClient } from "@tanstack/react-query";

const POLL_INTERVAL_MS = 3_000;

/**
 * When running inside the TWA, checks on each navigation whether a device
 * token is stored. If it is missing:
 *   - If the TWA port is already open, sends REQUEST_DEVICE_TOKEN directly.
 *   - Otherwise, polls ALL_ABOARD until Android replies TICKETS_PLEASE, then
 *     sends REQUEST_DEVICE_TOKEN.
 *
 * The incoming "deviceToken:<token>" message is persisted by entry.client.tsx
 * (independent of React mounting). This hook is the belt-and-suspenders layer.
 */
export const useRequestDeviceToken = () => {
  const { isTwa } = useTwa();
  const isHydrated = useHydrated();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Persist any incoming deviceToken messages and invalidate the React Query
  // cache so useNativeSubscription picks up the new value immediately.
  useEffect(() => {
    if (!isHydrated || !isTwa) {
      return;
    }

    const handleTwaMessage = (event: Event) => {
      if (!(event instanceof CustomEvent) || typeof event.detail !== "string") {
        return;
      }

      const { detail } = event;
      if (
        typeof detail === "string" &&
        detail.startsWith(DEVICE_TOKEN_PREFIX)
      ) {
        const token = detail.slice(DEVICE_TOKEN_PREFIX.length);
        if (token) {
          datadogRum.addAction("twa.device_token_received_via_postmessage");
          sharedLocalStorage.deviceToken.set(token).then(() => {
            void queryClient.invalidateQueries({
              queryKey: [DEVICE_TOKEN_QUERY_KEY, "local"],
            });
          });
        }
      }
    };

    window.addEventListener("twa-message", handleTwaMessage);
    return () => window.removeEventListener("twa-message", handleTwaMessage);
  }, [isHydrated, isTwa, queryClient]);

  // On each navigation or when the TWA channel first opens, check if the
  // token is missing and request one if so.
  useEffect(() => {
    if (!isHydrated || !isTwa || !isTwaTwoWayCommunicationSupported()) {
      return;
    }

    let active = true;
    let pollIntervalId: ReturnType<typeof setInterval> | undefined;
    let handshakeListener: ((e: Event) => void) | undefined;

    const cleanup = () => {
      active = false;
      clearInterval(pollIntervalId);
      if (handshakeListener) {
        window.removeEventListener("twa-message", handshakeListener);
        handshakeListener = undefined;
      }
    };

    const getAndroidDeviceToken = async () => {
      const token = await sharedLocalStorage.deviceToken.get();
      if (!active || token) {
        return;
      }

      console.debug("[TWA] Device token missing, requesting…");
      datadogRum.addAction("twa.device_token_missing", {
        pathname: location.pathname,
      });

      // If the port is already open, skip the handshake — but first stop any
      // in-progress handshake polling so it doesn't keep sending ALL_ABOARD.
      if (isTwaPortReady()) {
        clearInterval(pollIntervalId);
        pollIntervalId = undefined;
        if (handshakeListener) {
          window.removeEventListener("twa-message", handshakeListener);
          handshakeListener = undefined;
        }
        datadogRum.addAction("twa.request_device_token", { method: "direct" });
        sendTwaMessage("REQUEST_DEVICE_TOKEN");
        return;
      }

      // Port not open yet — do the ALL_ABOARD handshake.
      handshakeListener = (event: Event) => {
        if (
          !(event instanceof CustomEvent) ||
          typeof event.detail !== "string"
        ) {
          return;
        }

        const { detail } = event;
        if (detail === "TICKETS_PLEASE" && active) {
          cleanup();
          datadogRum.addAction("twa.request_device_token", {
            method: "handshake",
          });
          sendTwaMessage("REQUEST_DEVICE_TOKEN");
        }
      };

      window.addEventListener("twa-message", handshakeListener);
      sendTwaMessage("ALL_ABOARD");
      pollIntervalId = setInterval(
        () => sendTwaMessage("ALL_ABOARD"),
        POLL_INTERVAL_MS,
      );
    };

    // Also fire immediately when the port first becomes available, so we
    // don't have to wait for the next navigation if the channel opens after
    // the initial render.
    const onChannelReady = () => {
      void getAndroidDeviceToken();
    };

    window.addEventListener("twa-channel-ready", onChannelReady);
    void getAndroidDeviceToken();

    return () => {
      cleanup();
      window.removeEventListener("twa-channel-ready", onChannelReady);
    };
  }, [isHydrated, isTwa, location.pathname]);
};
